import { supabase } from './supabase';
import { toast } from './toast';
import { isNetworkError } from './errorHandling';

const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000;
const MAX_RETRY_DELAY = 5000;
const SESSION_BUFFER_TIME = 10 * 60 * 1000; // 10 minutes in milliseconds

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const isSessionValid = (session: any) => {
  if (!session) return false;
  const expiresAt = new Date((session.expires_at || 0) * 1000);
  if (!expiresAt) return false;
  return expiresAt > new Date(Date.now() + SESSION_BUFFER_TIME);
};

export const clearAuthData = () => {
  try {
    if (typeof localStorage !== 'undefined') {
      // Clear all Supabase-specific items
      localStorage.removeItem('supabase.auth.token');
      localStorage.removeItem('supabase.auth.refreshToken');
      localStorage.removeItem('supabase.auth.expires_at');
      localStorage.removeItem('supabase.auth.provider');
      
      // Clear custom auth items
      localStorage.removeItem('pm-pro-auth-token');
      localStorage.removeItem('pm-pro-auth-user');
      
      // Clear any other potential auth data
      const keys = Object.keys(localStorage);
      for (const key of keys) {
        if (key.includes('auth') || key.includes('supabase')) {
          localStorage.removeItem(key);
        }
      }
    }
    
    if (typeof sessionStorage !== 'undefined') {
      // Clear session storage
      sessionStorage.clear();
    }
  } catch (error) {
    console.error('Error clearing auth data:', error);
  }
};

export const handleAuthError = async () => {
  try {
    // Don't redirect if already on auth-related pages
    const currentPath = window.location.pathname;
    const authPaths = ['/auth', '/', '/overview'];
    const isAuthPath = authPaths.includes(currentPath);

    // Clear all auth data first
    clearAuthData();
    
    // Attempt to sign out from Supabase
    await supabase.auth.signOut().catch(() => {
      // Ignore signOut errors since we've already cleared local data
    });
    
    // Only redirect if not already on auth-related pages
    if (!isAuthPath) {
      window.location.href = '/auth';
    }
  } catch (error) {
    console.error('Error handling auth error:', error);
    // Ensure we still clear data and redirect even if there's an error
    clearAuthData();
    if (!['/auth', '/', '/overview'].includes(window.location.pathname)) {
      window.location.href = '/auth';
    }
  }
};

export const refreshSession = async () => {
  let retries = 0;
  const currentPath = window.location.pathname;
  const isAuthPath = ['/auth', '/', '/overview'].includes(currentPath);

  // Check if localStorage is accessible
  try {
    if (typeof localStorage === 'undefined') {
      throw new Error('LocalStorage is not available');
    }
    // Test localStorage access
    localStorage.setItem('test', 'test');
    localStorage.removeItem('test');
  } catch (error) {
    console.error('LocalStorage is not accessible:', error);
    if (!isAuthPath) {
      window.location.href = '/auth';
    }
    return null;
  }

  while (retries < MAX_RETRIES) {
    try {
      // Try to get existing session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();

      // Handle session retrieval errors
      if (sessionError) {
        console.error('Session retrieval error:', sessionError);
        
        if (isRefreshTokenError(sessionError)) {
          await handleAuthError();
          return null;
        }
        await handleAuthError();
        return null;
      }

      // Validate session data structure
      if (!sessionData || typeof sessionData !== 'object') {
        console.error('Invalid session data structure:', sessionData);
        await handleAuthError();
        return null;
      }

      const { session: existingSession } = sessionData;

      // If we have a valid session, return it
      if (existingSession && isSessionValid(existingSession)) {
        return existingSession;
      }

      // Try to refresh the session
      const { data: refreshData, error: refreshError } = 
        await supabase.auth.refreshSession();

      // Handle refresh errors
      if (refreshError) {
        console.error('Session refresh error:', refreshError);
        
        if (isRefreshTokenError(refreshError)) {
          await handleAuthError();
          return null;
        }
        await handleAuthError();
        return null;
      }

      // Validate refresh data structure
      if (!refreshData || typeof refreshData !== 'object') {
        console.error('Invalid refresh data structure:', refreshData);
        await handleAuthError();
        return null;
      }

      const { session: refreshedSession } = refreshData;

      // If refresh succeeded but no session was returned
      if (!refreshedSession) {
        console.error('No session returned after refresh');
        await handleAuthError();
        return null;
      }

      return refreshedSession;

    } catch (error: any) {
      console.error('Session refresh attempt error:', error);
      retries++;

      // Handle network errors with retry
      if (isNetworkError(error) && retries < MAX_RETRIES) {
        const delay = Math.min(INITIAL_RETRY_DELAY * Math.pow(2, retries - 1), MAX_RETRY_DELAY);
        console.log(`Retrying session refresh in ${delay}ms...`);
        await wait(delay);
        continue;
      }

      // Handle auth-specific errors
      if (isRefreshTokenError(error) || isAuthError(error)) {
        await handleAuthError();
        return null;
      }

      // Log unexpected errors
      console.error('Unexpected session refresh error:', error);
      await handleAuthError();
      return null;
    }
  }

  // If we've exhausted retries
  console.error('Max retries reached for session refresh');
  await handleAuthError();
  return null;
};

// Helper function to check for refresh token errors
const isRefreshTokenError = (error: any): boolean => {
  if (!error) return false;
  
  const errorMessage = error.message?.toLowerCase() || '';
  const errorCode = error.code?.toLowerCase() || '';
  
  return (
    errorMessage.includes('refresh_token_not_found') ||
    errorMessage.includes('invalid refresh token') ||
    errorCode.includes('refresh_token_not_found') ||
    error.status === 400
  );
};

// Helper function to check for other auth errors
const isAuthError = (error: any): boolean => {
  if (!error) return false;
  
  const errorMessage = error.message?.toLowerCase() || '';
  
  return (
    errorMessage.includes('session_not_found') ||
    errorMessage.includes('jwt expired') ||
    errorMessage.includes('invalid jwt') ||
    error.status === 403
  );
};

export const updateProfile = async (updates: any) => {
  try {
    const session = await refreshSession();
    if (!session) {
      throw new Error('No valid session');
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('No authenticated user');
    }

    const { error } = await supabase
      .from('profiles')
      .upsert({
        ...updates,
        user_id: user.id,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id'
      });

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error updating profile:', error);
    toast.error('Failed to update profile');
    return false;
  }
};

export const deleteAccount = async () => {
  try {
    const session = await refreshSession();
    if (!session) {
      throw new Error('No valid session');
    }

    // Call the delete_user RPC function
    const { error } = await supabase.rpc('delete_user');
    if (error) {
      throw error;
    }

    try {
      await supabase.auth.signOut();
    } finally {
      clearAuthData();
      window.location.href = '/auth';
    }
    return true;
  } catch (error) {
    console.error('Error deleting account:', error);
    throw error;
  }
};